/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';

import { TProducer } from '@global-types/api/api';
import {
	TQueryParams,
	TSortStatus,
} from '@global-types/index';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IProducerSlice {
	producers: TProducer[];
	producersTotal: number;
	producersPage: number;
	producersPageSize: number;
	producersTotalPages: number;
	producersSelectedRecords: TProducer[];
	producersQueryParams: TQueryParams;
	producersSelectedRow: Partial<TProducer>;
	producersSortParams: string;
	producerOrganization: string | null;
	producersSortStatus: {};
	producersImportErrors: string | null;
	producersLastAccessTokenCreated: string | null;
	producersOpenConfirmationModalDelete: {
		open: boolean;
		params: string;
	};
	producersOpenConfirmationModalVerify: {
		open: boolean;
		params: string;
	};
	producersOpenConfirmationModalMint: {
		open: boolean;
		params: string;
	};

	setProducersLastAccessTokenCreated: (
		_producersLastAccessTokenCreated: string
	) => void;

	setProducersImportErrors: (_errors: string) => void;

	setProducerOrganization: (
		_organizationId: string | null
	) => void;

	setProducersPage: (_producersPage: number) => void;

	setProducersSelectedRecords: (
		_producersSelectedRecords: TProducer[]
	) => void;

	setProducersOpenConfirmationModalDelete: (_producersOpenConfirmationModalDelete: {
		open: boolean;
		params: any;
	}) => void;

	setProducersOpenConfirmationModalVerify: (_producersOpenConfirmationModalVerify: {
		open: boolean;
		params: any;
	}) => void;
	setProducersOpenConfirmationModalMint: (_producersOpenConfirmationModalMint: {
		open: boolean;
		params: any;
	}) => void;

	setProducersQueryParams: (
		_producersQueryParams: TQueryParams
	) => void;

	setProducersSortStatus: (
		_producersSortStatus: TSortStatus
	) => void;
	setProducersSortParams: (
		_producersSortParams: string
	) => void;

	setProducersSelectedRow: (_producer: any) => void;
	setProducersPageSize: (_pageSize: number) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	producers: [],
	producersTotal: 0,
	producersPage: 1,
	producersPageSize: 100,
	producersTotalPages: 0,
	producersSelectedRecords: [],
	producersQueryParams: {},
	producersSelectedRow: {},
	producersSortParams: '-createdAt',
	producerOrganization: null,
	producersSortStatus: {},
	producersImportErrors: null,
	producersLastAccessTokenCreated: null,
	producersOpenConfirmationModalDelete: {
		open: false,
		params: '',
	},
	producersOpenConfirmationModalVerify: {
		open: false,
		params: '',
	},
	producersOpenConfirmationModalMint: {
		open: false,
		params: '',
	},
};

export const createProducersSlice: StateCreator<
	TStore,
	[],
	[],
	IProducerSlice
> = (set) => ({
	...initialState,
	setProducerOrganization: (
		_organizationId: string | null
	) => set({ producerOrganization: _organizationId }),

	setProducersLastAccessTokenCreated: (
		_producersLastAccessTokenCreated: string
	) =>
		set({
			producersLastAccessTokenCreated:
				_producersLastAccessTokenCreated,
		}),

	setProducersImportErrors: (_errors: string) =>
		set({ producersImportErrors: _errors }),

	setProducersSelectedRow: (_producer: TProducer) =>
		set({ producersSelectedRow: _producer }),

	setProducersSortParams: (_sortStatus: string) =>
		set({ producersSortParams: _sortStatus }),

	setProducersSortStatus: (_sortStatus: TSortStatus) =>
		set({ producersSortStatus: _sortStatus }),

	setProducersQueryParams: (_queryParams: TQueryParams) =>
		set({ producersQueryParams: _queryParams }),
	setProducersTotal: (_total: number) =>
		set({ producersTotal: _total }),
	setProducersOpenConfirmationModalDelete:
		(_producersOpenConfirmationModalDelete: {
			open: boolean;
			params: any;
		}) =>
			set({
				producersOpenConfirmationModalDelete:
					_producersOpenConfirmationModalDelete,
			}),
	setProducersOpenConfirmationModalMint:
		(_producersOpenConfirmationModalMint: {
			open: boolean;
			params: string;
		}) =>
			set({
				producersOpenConfirmationModalMint:
					_producersOpenConfirmationModalMint,
			}),
	setProducersOpenConfirmationModalVerify:
		(_producersOpenConfirmationModalVerify: {
			open: boolean;
			params: string;
		}) =>
			set({
				producersOpenConfirmationModalVerify:
					_producersOpenConfirmationModalVerify,
			}),
	setProducersPage: (_page: number) =>
		set({ producersPage: _page }),
	setProducersPageSize: (_pageSize: number) =>
		set({ producersPageSize: _pageSize }),
	setProducersTotalPages: (_totalPages: number) =>
		set({ producersTotalPages: _totalPages }),
	setProducersSelectedRecords: (
		_producersSelectedRecords: TProducer[]
	) =>
		set({
			producersSelectedRecords: _producersSelectedRecords,
		}),
});
